import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AuthContext from "../../context/authProvider";
import ToastContext from "../../context/toastProvider";
import Loading from "../atoms/loading";
import Button from "../atoms/button";
import ReviewSlabPanel from "./ReviewSlabPanel";
import EditOutsidePanel from "./EditOutsidePanel";
import SourceCropPreview from "./SourceCropPreview";
import GenerationHistory, { recipeSummary } from "./GenerationHistory";
import { tokenUserId } from "./tokenUser";
import { CHECKLIST, checklistComplete, emptyChecklist } from "./reviewChecklist";
import { DEFAULT_GEOMETRY, frameCropDefault, ERA_OPTIONS } from "./artLabelPreviewMath";
import { statusLabel, sourceLabel } from "./status";
import {
    getArtLabel, getArtLabelGeometry, patchArtLabel, generateArtLabel, reconcileArtLabel,
    approveArtLabel, rejectArtLabel, getArtLabelPromptDefaults,
} from "./functions";
import { rankVariations } from "./variationRank";

const POLL_MS = 5000;
const BUSY_STATUSES = ["queued", "generating"];
const REJECTABLE_STATUSES = ["review", "failed"];
const CROP_FIELD_LABEL = { top: "topo", bottom: "base", left: "esquerda", right: "direita" };
const FULL_CARD = { top: 0, bottom: 1, left: 0, right: 1 };
// Continuação é o padrão desde 2026-10-01; sem sujeito + expansão e abstrato seguem como alternativas.
const METHOD_OPTIONS = [["outpaint-up", "continuação para cima (padrão)"], ["ace", "sem sujeito + expansão e abstrato"], ["abstract", "só abstrato (sem IA, custo zero)"]];
const METHOD_PAYLOAD = { "outpaint-up": ["outpaint-up"], ace: ["remove-expand", "abstract"], abstract: ["abstract"] };
// Chave de GET /prompt-defaults de cada opção (o abstrato do lote ACE não usa prompt).
const DEFAULTS_KEY = { "outpaint-up": "outpaint-up", ace: "remove-expand", abstract: "abstract" };
const GENERATE_ERRORS = {
    INVALID_PROMPT: "Prompt inválido: o prompt aceita até 2000 caracteres e o negativo até 1000",
    INVALID_SEED: "Seed inválida: use um número inteiro maior ou igual a zero, ou deixe vazio para aleatória",
};

const pct = (v) => `${Math.round(Number(v) * 100)}%`;

/** Topo do ranking — é a variação que a tela seleciona sozinha, a mesma que leva o selo "recomendada". */
const topRanked = (doc) => {
    const top = rankVariations(doc?.generations || [])[0];
    return top ? { generationId: top.generationId, outputIndex: top.outputIndex } : null;
};

/** A seleção do revisor continua existindo no documento recarregado? */
const selectionExists = (doc, selection) => Boolean(
    selection && (doc?.generations || []).some((g) => g.id === selection.generationId && (g.outputs || [])[selection.outputIndex])
);

/**
 * Custo estimado do lote: abstrato roda local; a continuação cobra o canvas cheio (~1024 × 1409,
 * 2 MP arredondados) por imagem, ~US$ 0,08 na média de FLUX.2 (0,03/MP) e Bria (0,053/MP);
 * sem sujeito + expansão soma a separação.
 */
const estimatedCost = (methods, numImages) => {
    const n = Number(numImages);
    if (methods === "abstract") return 0;
    if (methods === "outpaint-up") return 0.08 * n;
    return 0.08 + 0.035 * n;
};

/** O prompt só chega aos providers de imagem: o abstrato e o motivo do set não o leem. */
const methodUsesPrompt = (methods, setMotif) => !setMotif && methods !== "abstract";
const SEED_RE = /^\d+$/;

const ArtLabelReview = () => {
    const { artKey = "" } = useParams(); // react-router já entrega o parâmetro decodificado
    const { user } = useContext(AuthContext);
    const { goodToast, badToast } = useContext(ToastContext);

    const [geometry, setGeometry] = useState(DEFAULT_GEOMETRY);
    const [label, setLabel] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(null); // { generationId, outputIndex }
    const [frameType, setFrameType] = useState("standard");
    const [crop, setCrop] = useState(frameCropDefault("standard"));
    const [era, setEra] = useState("sv");
    const [prompt, setPrompt] = useState("");
    const [promptEdited, setPromptEdited] = useState(false);
    const [negative, setNegative] = useState("");
    const [negativeEdited, setNegativeEdited] = useState(false);
    const [seed, setSeed] = useState("");
    const [promptDefaults, setPromptDefaults] = useState(null);
    const [defaultsFailed, setDefaultsFailed] = useState(false);
    const [numImages, setNumImages] = useState(2);
    const [methods, setMethods] = useState("outpaint-up");
    const [subjectWarning, setSubjectWarning] = useState(false);
    const [checklist, setChecklist] = useState(emptyChecklist());
    const [note, setNote] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const initialLoaded = useRef(false);
    const prevStatus = useRef(null);

    const load = useCallback(async () => {
        const out = await getArtLabel(user?.token, artKey);
        if (out?.type !== "success") {
            // Só a primeira carga derruba a tela; falha de polling/refresh vira toast.
            if (initialLoaded.current) badToast(out?.message || "Falha ao atualizar a arte");
            else setError(out?.message || "Arte não encontrada");
            return null;
        }
        initialLoaded.current = true;
        setError("");
        setLabel(out.data);
        return out.data;
    }, [user?.token, artKey, badToast]);

    useEffect(() => {
        getArtLabelGeometry(user?.token).then((out) => { if (out?.type === "success" && out.data) setGeometry(out.data); });
    }, [user?.token]);

    useEffect(() => {
        Promise.resolve(getArtLabelPromptDefaults(user?.token)).then((out) => {
            if (out?.type === "success" && out.data?.methods) setPromptDefaults(out.data);
            else setDefaultsFailed(true);
        }).catch(() => setDefaultsFailed(true));
    }, [user?.token]);

    const defaultsFor = useCallback((option) => promptDefaults?.methods?.[DEFAULTS_KEY[option]] || null, [promptDefaults]);
    const methodDefaults = useMemo(() => defaultsFor(methods), [defaultsFor, methods]);
    // E1/E2: padrão do método escolhido; só sobrescreve o que o operador não editou.
    useEffect(() => {
        if (!promptEdited) setPrompt(methodDefaults?.prompt || "");
        if (!negativeEdited) setNegative(methodDefaults?.negativePrompt || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [methodDefaults]);

    useEffect(() => {
        load().then((doc) => {
            if (!doc) return;
            const docEra = doc.rules?.era || "sv";
            setFrameType(doc.frameType || "standard");
            setEra(docEra);
            setCrop(doc.crop || frameCropDefault(doc.frameType, docEra));
            // Mantém o que o revisor já escolheu; sem seleção válida, cai na recomendada.
            setSelected((current) => (selectionExists(doc, current) ? current : topRanked(doc)));
        });
    }, [load]);

    // D7 — ao sair de gerando/na fila para em revisão, seleciona sozinha a variação recomendada.
    useEffect(() => {
        if (!label) return;
        const previous = prevStatus.current;
        prevStatus.current = label.status;
        if (!BUSY_STATUSES.includes(previous) || label.status !== "review") return;
        const top = topRanked(label);
        if (!top) return;
        setSelected(top);
        setChecklist(emptyChecklist());
    }, [label]);

    // Polling enquanto a fila do fal trabalha.
    useEffect(() => {
        if (!label || !BUSY_STATUSES.includes(label.status)) return undefined;
        const t = setInterval(() => { load(); }, POLL_MS);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [label?.status, load]);

    const selectedOutput = useMemo(() => {
        if (!label || !selected) return null;
        const gen = (label.generations || []).find((g) => g.id === selected.generationId);
        const out = gen && gen.outputs && gen.outputs[selected.outputIndex];
        return out ? { ...out, canvas: gen.inputs?.canvas, gen } : null;
    }, [label, selected]);
    const ranked = useMemo(() => rankVariations(label?.generations || []), [label]);
    // Lote mais novo da IA: correção não é lote de geração (não tem fundo, janela nem causa).
    const latestBatch = useMemo(() => {
        const gens = (label?.generations || []).filter((g) => g.method !== "correction");
        const newest = gens[gens.length - 1];
        if (!newest) return [];
        return newest.batchId ? gens.filter((g) => g.batchId === newest.batchId) : [newest];
    }, [label]);

    const run = async (fn, okMsg, errorMsg = () => null) => {
        setBusy(true);
        try {
            const out = await fn();
            if (out?.type === "success") { if (okMsg) goodToast(okMsg); await load(); return out; }
            badToast(errorMsg(out) || out?.message || "Falha na operação");
            return null;
        } finally {
            setBusy(false);
        }
    };

    const selectVariation = (generationId, outputIndex) => {
        setSelected({ generationId, outputIndex });
        setChecklist(emptyChecklist());
    };

    // O backend compõe a geração a partir do documento salvo: ajuste só na tela não conta.
    const dirty = !!label && (frameType !== label.frameType || JSON.stringify(crop) !== JSON.stringify(label.crop) || era !== (label.rules?.era || "sv"));
    const setMotif = Boolean(label?.rules?.setMotif);
    const usesPrompt = methodUsesPrompt(methods, setMotif);
    const methodProviders = methodDefaults?.providers || [];
    const usesNegative = usesPrompt && methodProviders.some((p) => p.usesNegativePrompt);
    const usesSeed = !setMotif && methodProviders.some((p) => p.usesSeed);
    const noPromptProviders = methodProviders.filter((p) => !p.usesPrompt);

    const saveAdjustments = () => run(() => patchArtLabel(user?.token, artKey, { frameType, crop, rules: { era } }), "Ajustes salvos");
    const regenerate = async () => {
        const seedText = String(seed).trim();
        if (usesSeed && seedText && !SEED_RE.test(seedText)) { badToast(GENERATE_ERRORS.INVALID_SEED); return; }
        if (dirty) {
            setBusy(true);
            let saved;
            try {
                saved = await patchArtLabel(user?.token, artKey, { frameType, crop, rules: { era } });
            } finally {
                setBusy(false);
            }
            if (saved?.type !== "success") { badToast(saved?.message || "Falha ao salvar os ajustes"); return; }
        }
        const body = {
            numImages: Number(numImages),
            methods: METHOD_PAYLOAD[methods],
            ...(usesPrompt ? { promptOverride: prompt } : {}),
            ...(usesNegative ? { negativePromptOverride: negative } : {}),
            ...(usesSeed && seedText ? { seed: Number(seedText) } : {}),
        };
        const out = await run(() => generateArtLabel(user?.token, artKey, body), "Geração enviada", (res) => GENERATE_ERRORS[res?.code]);
        if (out) { setChecklist(emptyChecklist()); setSubjectWarning(Boolean(out.data?.subjectNotSeparable)); }
    };
    const reconcile = () => { setMenuOpen(false); return run(() => reconcileArtLabel(user?.token, artKey), "Reconciliado"); };
    const approve = async () => {
        const out = await run(() => approveArtLabel(user?.token, artKey, { source: "ai", generationId: selected.generationId, outputIndex: selected.outputIndex }), "Arte aprovada");
        if (out) setChecklist(emptyChecklist());
    };
    const reject = async () => {
        const out = await run(() => rejectArtLabel(user?.token, artKey, note.trim()), "Arte rejeitada");
        if (out) { setNote(""); setRejectOpen(false); }
    };
    // A correção entra como variação nova: recarrega e já a deixa selecionada para o checklist.
    const onCorrectionAdded = async (generationId) => {
        const doc = await load();
        if (!doc || !generationId) return;
        setSelected({ generationId, outputIndex: 0 });
        setChecklist(emptyChecklist());
    };

    const editPrompt = (value) => { setPrompt(value); setPromptEdited(true); };
    const editNegative = (value) => { setNegative(value); setNegativeEdited(true); };
    const restorePrompt = () => { setPrompt(methodDefaults?.prompt || ""); setPromptEdited(false); };
    const restoreNegative = () => { setNegative(methodDefaults?.negativePrompt || ""); setNegativeEdited(false); };
    // E6: "Repetir com estas configurações" preenche o passo Gerar e os ajustes da fonte (salvos ao gerar).
    const repeatRecipe = (r) => {
        const defs = defaultsFor(r.methodOption);
        setMethods(r.methodOption);
        setNumImages(r.numImages);
        setPrompt(r.prompt ?? (defs?.prompt || ""));
        setPromptEdited(r.prompt != null);
        setNegative(r.negativePrompt ?? (defs?.negativePrompt || ""));
        setNegativeEdited(r.negativePrompt != null);
        setSeed(r.seed != null ? String(r.seed) : "");
        if (r.source) {
            const nextEra = r.source.era && r.source.era !== "unknown" ? r.source.era : era;
            const nextFrame = r.source.frameType || frameType;
            setFrameType(nextFrame);
            setEra(nextEra);
            setCrop(r.source.crop ? { ...r.source.crop } : frameCropDefault(nextFrame, nextEra));
        }
        goodToast("Configurações copiadas para o passo Gerar");
    };
    const usePrompt = (text) => { editPrompt(text); goodToast("Prompt copiado para o passo Gerar"); };

    const setCropField = (field, value) => setCrop((c) => ({ ...c, [field]: Number(value) }));
    const changeFrame = (value) => { setFrameType(value); setCrop(frameCropDefault(value, era)); };
    const changeEra = (next) => { setEra(next); setCrop(frameCropDefault(frameType, next)); };

    if (error) return <div className="p-4 text-red-400">{error} — <Link className="underline" to="/manafix/art-labels">voltar</Link></div>;
    if (!label) return <Loading />;

    const batchBackground = latestBatch.find((g) => g.inputs?.backgroundUrl) || null;
    const separationError = latestBatch.map((g) => g.inputs?.separationError).find(Boolean) || "";
    const cardImage = label.sourceImage?.signedUrl || label.sourceImage?.url || null;
    const isBusyStatus = BUSY_STATUSES.includes(label.status);
    const checklistOk = checklistComplete(checklist);
    const canApprove = !!selected && checklistOk && !busy && !isBusyStatus;
    const rejectable = REJECTABLE_STATUSES.includes(label.status);
    const canReject = rejectable && !!note.trim() && !busy;

    return (
        <div className="p-4 text-left text-sm">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <Link className="underline text-[#d59106]" to="/manafix/art-labels">← Artes</Link>
                    <h2 className="text-orange-500 text-xl mt-1">{label.cardName} <span className="text-gray-400 text-base">· {label.setName || label.setId}</span></h2>
                    <p className="text-gray-400">
                        <code>{label.artKey}</code> · status: <strong className={isBusyStatus ? "animate-pulse" : ""}>{statusLabel(label.status)}</strong>
                        {label.approved ? <> · aprovada v{label.approved.version} ({sourceLabel(label.approved.source)})</> : null}
                        {label.approved?.recipe ? (
                            <span data-testid="approved-recipe" title={[label.approved.recipe.model, label.approved.recipe.prompt].filter(Boolean).join("\n") || undefined}> · receita: {recipeSummary(label.approved.recipe)}</span>
                        ) : null}
                    </p>
                </div>
                <div className="relative">
                    <button
                        type="button"
                        aria-label="Mais ações"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        className="px-2 text-xl leading-none text-[#ffedd5]"
                        onClick={() => setMenuOpen((open) => !open)}
                    >
                        ⋯
                    </button>
                    {menuOpen ? (
                        <div role="menu" className="absolute right-0 z-10 min-w-[10rem] bg-[#111] border border-[#b47c06]">
                            <button type="button" role="menuitem" className="block w-full text-left px-3 py-1 hover:bg-[#2a2a2a]" onClick={reconcile} disabled={busy}>Reconciliar</button>
                            <button
                                type="button"
                                role="menuitem"
                                className="block w-full text-left px-3 py-1 hover:bg-[#2a2a2a] disabled:opacity-50"
                                onClick={() => { setMenuOpen(false); setRejectOpen(true); }}
                                disabled={!rejectable}
                                title={rejectable ? undefined : "Só em revisão ou falhou"}
                            >
                                Rejeitar…
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>

            {rejectOpen ? (
                <div className="border border-red-700 p-2 mt-2">
                    <input className="w-full text-black px-2" placeholder="Motivo da rejeição (obrigatório)" value={note} onChange={(e) => setNote(e.target.value)} />
                    <div className="flex items-center gap-2 mt-2">
                        <Button classes="bg-red-500 hover:bg-red-800" click={reject} disabled={!canReject}>Confirmar rejeição</Button>
                        <button type="button" className="underline" onClick={() => { setRejectOpen(false); setNote(""); }}>Cancelar</button>
                    </div>
                </div>
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <ReviewSlabPanel
                    key={label.artKey}
                    label={label}
                    geometry={geometry}
                    token={user?.token}
                    ranked={ranked}
                    selected={selected}
                    selectedOutput={selectedOutput}
                    onSelect={selectVariation}
                />

                <div className="space-y-4">
                    <fieldset className="border border-[#b47c06] p-2">
                        <legend className="text-[#d59106] px-1">1. Fonte</legend>
                        <div className="flex flex-wrap gap-3">
                            <SourceCropPreview imageUrl={cardImage} crop={frameType === "full_art" ? FULL_CARD : crop} />
                            <div className="flex-1 min-w-[12rem]">
                                <label className="block">Era da carta
                                    <select aria-label="Era da carta" className="text-black ml-2" value={era} onChange={(e) => changeEra(e.target.value)}>
                                        {ERA_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                </label>
                                <label className="block">Moldura
                                    <select aria-label="Moldura" className="text-black ml-2" value={frameType} onChange={(e) => changeFrame(e.target.value)}>
                                        <option value="standard">padrão (janela de arte)</option>
                                        <option value="full_art">full art (carta inteira)</option>
                                    </select>
                                </label>
                                {["top", "bottom", "left", "right"].map((f) => (
                                    <label key={f} className="block">{CROP_FIELD_LABEL[f]}: {pct(crop[f])}
                                        <input aria-label={`Recorte ${CROP_FIELD_LABEL[f]}`} type="range" min="0" max="1" step="0.01" value={crop[f]} onChange={(e) => setCropField(f, e.target.value)} className="w-full" disabled={frameType === "full_art"} />
                                    </label>
                                ))}
                            </div>
                        </div>
                        {latestBatch.length ? (batchBackground ? (
                            <div className="mt-2">
                                <p className="text-xs text-gray-400">Fundo sem sujeito do último lote{typeof batchBackground.inputs.coverage === "number" ? ` — máscara cobriu ${Math.round(batchBackground.inputs.coverage * 100)}%` : ""}</p>
                                <img alt="fundo sem sujeito" src={batchBackground.inputs.backgroundUrl} className="max-h-24 border border-[#444]" />
                            </div>
                        ) : <p className="mt-2 text-xs text-gray-500">Sem fundo neste lote</p>) : null}
                        <Button classes="bg-[#d59106] hover:bg-[#b47c06] mt-2" click={saveAdjustments} disabled={busy || isBusyStatus}>Salvar ajustes</Button>
                    </fieldset>

                    <fieldset className="border border-[#b47c06] p-2">
                        <legend className="text-[#d59106] px-1">2. Gerar</legend>
                        {setMotif ? (
                            <p className="text-xs text-gray-400">Motivo do set: a arte vem pronta, sem IA</p>
                        ) : (
                            <>
                                <label className="block">Métodos
                                    <select aria-label="Métodos" className="text-black ml-2" value={methods} onChange={(e) => setMethods(e.target.value)}>
                                        {METHOD_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                </label>
                                <label className="block mt-1">Variações
                                    <select aria-label="Variações" className="text-black ml-2" value={numImages} onChange={(e) => setNumImages(e.target.value)}>
                                        {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    <span className="text-gray-500 ml-2">≈ US$ {estimatedCost(methods, numImages).toFixed(2)}</span>
                                </label>
                            </>
                        )}
                        {usesPrompt ? (
                            <div className="mt-1">
                                <div className="flex items-center justify-between">
                                    <span>Prompt</span>
                                    <button type="button" aria-label="Restaurar prompt padrão" className="underline text-xs text-[#d59106]" onClick={restorePrompt}>Restaurar padrão</button>
                                </div>
                                <textarea aria-label="Prompt" className="w-full text-black px-2" rows={4} maxLength={2000} value={prompt} onChange={(e) => editPrompt(e.target.value)} />
                                <p className="text-xs text-gray-500">Se deixar vazio, usa o prompt padrão do método.</p>
                                {defaultsFailed ? <p className="text-xs text-amber-300">Padrões de prompt indisponíveis; usando o prompt do servidor.</p> : null}
                                {methodProviders.length && noPromptProviders.length === methodProviders.length ? (
                                    <p className="text-xs text-amber-300">Os provedores deste método não usam prompt; o texto fica só na receita.</p>
                                ) : noPromptProviders.map((p) => (
                                    <p key={p.provider} className="text-xs text-amber-300">{p.provider}: este provedor não usa prompt</p>
                                ))}
                            </div>
                        ) : null}
                        {usesNegative ? (
                            <div className="mt-1">
                                <div className="flex items-center justify-between">
                                    <span>Prompt negativo</span>
                                    <button type="button" aria-label="Restaurar negativo padrão" className="underline text-xs text-[#d59106]" onClick={restoreNegative}>Restaurar padrão</button>
                                </div>
                                <textarea aria-label="Prompt negativo" className="w-full text-black px-2" rows={2} maxLength={1000} value={negative} onChange={(e) => editNegative(e.target.value)} />
                            </div>
                        ) : null}
                        {usesSeed ? (
                            <label className="block mt-1">Seed
                                <input aria-label="Seed" type="number" min="0" step="1" className="text-black ml-2 w-32 px-1" placeholder="aleatória" value={seed} onChange={(e) => setSeed(e.target.value)} />
                                <span className="text-gray-500 ml-2 text-xs">vazio = aleatória; só os provedores que aceitam</span>
                            </label>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            <Button classes="bg-blue-400 hover:bg-blue-600" click={regenerate} disabled={busy || isBusyStatus}>Regenerar</Button>
                            {dirty ? <span className="text-xs text-[#d59106]">Ajustes não salvos serão salvos antes de gerar</span> : null}
                        </div>
                        {subjectWarning ? <p data-testid="subject-warning" className="text-xs text-amber-300">Sujeito não separável nesta janela: só o candidato abstrato foi gerado. Ajuste o recorte, use a moldura full art ou edite fora do sistema.</p> : null}
                        {separationError ? <p data-testid="separation-error" className="text-xs text-amber-300">Causa: {separationError}</p> : null}
                    </fieldset>

                    <fieldset className="border border-[#b47c06] p-2">
                        <legend className="text-[#d59106] px-1">3. Revisar e aprovar</legend>
                        {CHECKLIST.map((item) => (
                            <label key={item.id} className="block">
                                <input type="checkbox" checked={!!checklist[item.id]} onChange={(e) => setChecklist((c) => ({ ...c, [item.id]: e.target.checked }))} /> {item.label}
                            </label>
                        ))}
                        <Button classes="bg-green-400 hover:bg-green-600 mt-2" click={approve} disabled={!canApprove}>Aprovar variação selecionada</Button>
                        <EditOutsidePanel
                            token={user?.token}
                            label={label}
                            selected={selected}
                            geometry={geometry}
                            reload={load}
                            disabled={busy || isBusyStatus}
                            onCorrectionAdded={onCorrectionAdded}
                        />
                    </fieldset>

                    <GenerationHistory label={label} currentUserId={tokenUserId(user?.token)} onRepeat={repeatRecipe} onUsePrompt={usePrompt} />
                </div>
            </div>
        </div>
    );
};

export default ArtLabelReview;
